import React, { useState, useRef, useEffect } from 'react'
import { Icon, Input, Spin, Modal, message } from 'antd'
import { getItemForMarkImproved, markItemImproved } from '../../../services/api/3dApi'
import { getUserList } from '../../../services/api/UserService'
import ScannedItemList from './ScannedItemList'
import SessionSidebar from './SessionSidebar'

// Trần một lượt quét — khớp E_SESSION_LIMIT của BE (contract §3.2, G7).
const SESSION_LIMIT = 300
const LIMIT_MESSAGE = `Một lượt tối đa ${SESSION_LIMIT} item. Hãy xác nhận lượt hiện tại trước khi quét tiếp.`

// Alert 300 dòng thì không đọc được — cắt còn 20 dòng đầu (contract §2).
const REJECTED_MAX_LINES = 20

// Lọc danh sách Người hoàn thiện theo hậu tố username của tổ.
// GET {REACT_APP_URL_AUTH}/users?username=_htvibe
const IMPROVER_USERNAME_FILTER = '_htvibe'

// Contract §1: FE gửi lên mã đã trim, đã bỏ '#' đầu, đã uppercase.
const normalize = (raw) => String(raw || '').trim().replace(/^#/, '').toUpperCase()

// '#' đã bị bỏ ở normalize nên regex chỉ còn <barcode_number>_<item_number>.
const isValidFormat = (code) => /^\d+_\d+$/.test(code)

const truncateLines = (lines, max) => (
    lines.length <= max
        ? lines
        : [...lines.slice(0, max), `… và ${lines.length - max} mã khác`]
)

function MarkImprovedPage() {
    const _input = useRef()
    // Chặn quét chồng lệnh: máy quét bắn rất nhanh, state `loading` trong closure không kịp.
    const _busy = useRef(false)

    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [valueInput, setValueInput] = useState('')
    const [errorInput, setErrorInput] = useState('')
    const [items, setItems] = useState([])
    const [listUser, setListUser] = useState([])
    const [loadingUser, setLoadingUser] = useState(false)
    const [improver, setImprover] = useState(undefined)

    const _focusScanInput = () => {
        if (!!_input.current) _input.current.focus()
    }

    const _resetScanInput = () => {
        setValueInput('')
        setErrorInput('')
        _focusScanInput()
    }

    /**
     * Mọi popup đều trả focus về ô quét trong onOk.
     * onOk PHẢI là arrow function — viết `onOk: _resetScanInput()` là gọi hàm ngay lúc
     * mở Modal chứ không phải đăng ký callback (lỗi đang có ở MarkErrorItemPage.js:57).
     */
    const _popup = ({ type = 'error', title = 'Lỗi', lines = [], onOk }) => {
        return Modal[type]({
            title,
            content: (
                <div className='PopupLines'>
                    {lines.map((line, index) => (
                        <div key={index} className={!line ? 'BlankLine' : ''}>{line}</div>
                    ))}
                </div>
            ),
            okText: 'Đóng',
            onOk: () => {
                if (typeof onOk === 'function') return onOk()
                _focusScanInput()
            },
        })
    }

    /**
     * Lỗi validate phía FE (sai định dạng) -> inline text đỏ, KHÔNG mở Modal.
     * Modal chiếm focus sang nút OK, nên ký tự của lần quét kế tiếp bắn vào nút và Enter cuối
     * chuỗi bấm OK luôn -> mất trắng một lần quét. Đây là khuôn mà 9 màn quét khác của repo
     * đang dùng (MarkErrorItemPage.js:42/137, MexPage.js:21, MarkQC.js:148 ...).
     */
    const _showInlineError = (msg) => {
        setValueInput('')
        setErrorInput(msg)
        _focusScanInput()
    }

    // Mã bị BE loại: xoá ô quét ngay, nhưng chỉ trả focus sau khi người dùng bấm OK.
    const _rejectScan = (msg) => {
        setValueInput('')
        _popup({ type: 'error', title: 'Lỗi', lines: [msg] })
    }

    const _notifyDuplicate = (code) => {
        message.warning(`Item ${code} đã tồn tại trong danh sách.`)
        _resetScanInput()
    }

    // Lần quét bị guard nuốt: PHẢI báo, nếu không công nhân mất mã mà không hề biết.
    const _notifyBusy = () => {
        message.warning('Đang xử lý mã trước — vui lòng quét lại mã này.')
        _resetScanInput()
    }

    const _handleChangeScan = (e) => {
        setValueInput(e.target.value)
        setErrorInput('')
    }

    const _handleScan = async (e) => {
        e.preventDefault()

        if (_busy.current) return _notifyBusy()

        const code = normalize(valueInput)
        if (!code) return

        if (!isValidFormat(code)) return _showInlineError('Item không đúng định dạng.')
        if (items.some(i => i.barcode_item === code)) return _notifyDuplicate(code)
        if (items.length >= SESSION_LIMIT) return _rejectScan(LIMIT_MESSAGE)

        _busy.current = true
        setLoading(true)

        try {
            const { data: response } = await getItemForMarkImproved(code)
            const { success, data, error } = Object.assign({}, response)

            // Lỗi nghiệp vụ về HTTP 200 nên phải kiểm success ở đây, không phải trong catch.
            // Hiện thẳng message BE trả, không ghép chuỗi (contract §3.1).
            if (!success) return _rejectScan(error)

            setItems(prev => [data, ...prev])
            _resetScanInput()
        } catch (err) {
            _rejectScan(err.message)
        } finally {
            _busy.current = false
            setLoading(false)
        }
    }

    const _handleRemoveItem = (barcodeItem) => {
        const next = items.filter(i => i.barcode_item !== barcodeItem)
        setItems(next)

        // Xoá hết dòng = phiên kết thúc. Không reset thì lượt sau mở ra đã có sẵn người được
        // chọn từ lượt trước — đúng nghĩa là một giá trị mặc định, trái yêu cầu.
        if (!next.length) setImprover(undefined)

        _focusScanInput()
    }

    // Xoá thủ công: người dùng chủ động bấm, xoá sạch.
    const _clearSession = () => {
        setItems([])
        setImprover(undefined)
        _resetScanInput()
    }

    /**
     * Kết thúc một lượt: CHỈ gỡ những mã đã thực sự nằm trong payload gửi đi.
     * Ô quét không bị khoá trong lúc POST bay, nên công nhân có thể đã quét thêm mã sau thời
     * điểm chụp snapshot. Xoá sạch bằng setItems([]) sẽ nuốt luôn các mã đó — chúng chưa từng
     * được gửi lên BE và sau đó cũng không còn trên màn hình.
     */
    const _finishSession = (sentCodes) => {
        setItems(prev => prev.filter(i => !sentCodes.includes(i.barcode_item)))
        setImprover(undefined)
        _resetScanInput()
    }

    const _handleClearSession = () => {
        if (!items.length) return

        Modal.confirm({
            title: 'Xóa danh sách phiên',
            content: `Xoá toàn bộ ${items.length} item đã quét? Danh sách này không khôi phục lại được.`,
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: () => _clearSession(),
            onCancel: () => _focusScanInput(),
        })
    }

    // Mọi phần tử rejected[] đã được BE tự prefix mã item — FE chỉ nối lại, không ghép chuỗi.
    const _rejectedLines = (rejected) => ([
        `${rejected.length} item KHÔNG ghi nhận được:`,
        ...truncateLines(rejected.map(i => i.message), REJECTED_MAX_LINES),
    ])

    const _handleConfirm = async () => {
        if (!improver || !items.length || submitting) return

        setSubmitting(true)

        // Chụp đúng danh sách gửi đi, để lúc kết thúc lượt chỉ gỡ đúng bấy nhiêu mã.
        const sentCodes = items.map(i => i.barcode_item)

        try {
            const { data: response } = await markItemImproved({
                barcode_items: sentCodes,
                improver,
            })
            const { success, data, error } = Object.assign({}, response)
            const { recorded = 0, improver: recordedFor, rejected = [] } = Object.assign({}, data)

            // Nhánh 3 — không ghi được item nào (recorded === 0): chỉ hiện danh sách bị loại,
            // KHÔNG báo thành công và KHÔNG xoá phiên. Đây là ca ta biết CHẮC BE không ghi gì,
            // nên danh sách vẫn đúng nguyên và là thứ quý nhất để giữ: công nhân sửa nguyên nhân
            // (vd chọn lại người hoàn thiện) rồi bấm xác nhận lại, không phải quét lại cả bó.
            if (!success) {
                return _popup({
                    type: 'error',
                    title: 'Không ghi nhận được item nào',
                    lines: !!rejected.length
                        ? _rejectedLines(rejected)
                        : [error || 'Không ghi nhận được item nào.'],
                })
            }

            const summary = `Đã ghi nhận ${recorded} item · người hoàn thiện: ${recordedFor || improver}`

            // Nhánh 1 — ghi trọn vẹn.
            if (!rejected.length) {
                return _popup({
                    type: 'success',
                    title: 'Đánh dấu hoàn thiện',
                    lines: [summary],
                    onOk: () => _finishSession(sentCodes),
                })
            }

            // Nhánh 2 — ghi nhận một phần (D-1): MỘT popup duy nhất gộp cả 2 vế.
            return _popup({
                type: 'warning',
                title: 'Đánh dấu hoàn thiện',
                lines: [summary, '', ..._rejectedLines(rejected)],
                onOk: () => _finishSession(sentCodes),
            })
        } catch (err) {
            // Lỗi mạng: không biết BE đã ghi hay chưa ⇒ GIỮ NGUYÊN danh sách để quét lại được.
            message.error(err.message)
        } finally {
            setSubmitting(false)
        }
    }

    const _fetchUser = async () => {
        setLoadingUser(true)

        try {
            // UserService dùng lớp axios bóc sẵn .data ⇒ chỉ bóc 1 lớp, khác 3dApi.
            // Giữ page/limit bên cạnh username: nếu API mặc định phân trang nhỏ thì bỏ limit sẽ
            // cắt cụt dropdown mà không báo gì — người cần chọn lại không có trong danh sách.
            const { data, message: mess, success } = await getUserList({
                page: 1,
                limit: 10000,
                username: IMPROVER_USERNAME_FILTER,
            })

            if (!success) return console.log(mess)

            const { users } = Object.assign({}, data)
            setListUser(!!users && users.length ? users : [])
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingUser(false)
        }
    }

    useEffect(() => {
        _fetchUser()
    }, [])

    // Sau khi chọn người hoàn thiện, trả focus về ô quét để select không giữ con trỏ.
    useEffect(() => {
        if (!!improver) _focusScanInput()
    }, [improver])

    // Lấy hết user, không lọc role.
    const userOptions = listUser
        .filter(i => !!i.username)
        .map(i => ({ name: i.username, value: i.username }))
    const hasItems = !!items.length

    return (
        <div className='MarkImprovedPage py-5'>
            <div className='col-12 mx-auto px-5'>
                <h1 className='PageTitle'>Đánh dấu hoàn thiện</h1>

                <div className='PageBody d-flex align-items-start mt-4'>
                    <div className='ContentLeft'>
                        <div className='position-relative'>
                            <Spin spinning={loading} tip='Đang kiểm tra...'>
                                <form onSubmit={_handleScan} className='formQC'>
                                    <Input
                                        value={valueInput.toUpperCase()}
                                        style={{ fontSize: 40, height: 'auto' }}
                                        ref={_input}
                                        autoFocus={true}
                                        placeholder='Nhập item cần đánh dấu'
                                        onChange={_handleChangeScan}
                                    />

                                    {
                                        !!valueInput &&
                                        <Icon
                                            type='close-circle'
                                            className='position-absolute cursor-pointer text-dark IconInput'
                                            onClick={_resetScanInput}
                                        />
                                    }

                                    {
                                        loading &&
                                        <Icon type='sync' spin className='position-absolute IconInput' />
                                    }
                                </form>

                                {errorInput && <div className="text-danger mt-3">{errorInput}</div>}
                            </Spin>
                        </div>

                        {
                            hasItems
                                ? <ScannedItemList items={items} onRemove={_handleRemoveItem} />
                                : <div className='EmptyHint'>
                                    Chưa quét item nào. Khối chọn người hoàn thiện sẽ hiện ra ở cột phải
                                    sau mã hợp lệ đầu tiên.
                                </div>
                        }
                    </div>

                    {
                        hasItems &&
                        <div className='ContentRight'>
                            <SessionSidebar
                                count={items.length}
                                limit={SESSION_LIMIT}
                                userOptions={userOptions}
                                loadingUser={loadingUser}
                                improver={improver}
                                submitting={submitting}
                                onChangeImprover={setImprover}
                                onConfirm={_handleConfirm}
                                onClear={_handleClearSession}
                            />
                        </div>
                    }
                </div>
            </div>
        </div>
    )
}

export default MarkImprovedPage
